import React from 'react';
import { Cpu, Zap, Shield, Wifi } from 'lucide-react';
export function TechSpecsSection() {
  const specs = [{
    icon: Cpu,
    title: 'Smart Sensors',
    description: 'Advanced aerial and pavement sensors detect both moisture and temperature. The system only activates when snow or ice is present.'
  }, {
    icon: Zap,
    title: 'Energy Efficient',
    description: 'Our automated controllers modulate heat output based on ambient conditions. Typical operation costs are just pennies per square foot per storm.'
  }, {
    icon: Shield,
    title: 'Industrial Grade',
    description: 'Built to withstand decades of Canadian winters. Our systems use premium materials and proven engineering for unmatched durability and reliable performance year after year.'
  }, {
    icon: Wifi,
    title: 'WiFi Enabled Controls',
    description: 'Monitor and control your system from your smartphone. Receive alerts, check status, and view energy usage statistics from anywhere.'
  }];
  return <section className="py-16 bg-slate-900 text-white md:py-24" data-id="element-1179">
      <div className="container mx-auto px-4 md:px-6" data-id="element-1180">
        <div className="max-w-4xl mx-auto" data-id="element-1181">
          <div className="text-center mb-12" data-id="element-1182">
            <div className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-sm font-medium text-primary-foreground mb-6" data-id="element-1183">
              <span className="flex h-2 w-2 rounded-full bg-primary mr-2" data-id="element-1184"></span>
              Technical Specifications
            </div>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-6" data-id="element-1185">
              Engineered for Performance
            </h2>
            <p className="text-lg text-slate-300" data-id="element-1186">
              SunFlow systems are sophisticated climate management solutions
              designed for the harsh Canadian climate.
            </p>
          </div>

          {/* 4 Grid Specs */}
          <div className="grid sm:grid-cols-2 gap-6 mb-12" data-id="element-1187">
            {specs.map((spec, index) => <div key={index} className="flex gap-4" data-id="element-1188">
                <div className="shrink-0" data-id="element-1189">
                  <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center border border-slate-700" data-id="element-1190">
                    <spec.icon className="h-5 w-5 text-primary" data-id="element-1191" />
                  </div>
                </div>
                <div data-id="element-1192">
                  <h3 className="font-bold text-white mb-1" data-id="element-1193">{spec.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed" data-id="element-1194">
                    {spec.description}
                  </p>
                </div>
              </div>)}
          </div>

          {/* System Details */}
          <div className="grid md:grid-cols-2 gap-4" data-id="element-1195">
            {/* Electric System */}
            <div className="rounded-xl border border-slate-700 bg-slate-800/50 overflow-hidden" data-id="element-1196">
              <div className="p-4" data-id="element-1197">
                <h3 className="font-bold text-lg text-white mb-3" data-id="element-1198">
                  Electric Heated Driveway Systems
                </h3>
                <div className="text-slate-300 space-y-3" data-id="element-1199">
                  <p data-id="element-1200">
                    Electric systems use embedded heating cables powered by your
                    home's electrical system. Ideal for most residential
                    driveways due to their efficiency and responsiveness.
                  </p>
                  <ul className="space-y-1 text-sm" data-id="element-1201">
                    <li className="flex items-center gap-2" data-id="element-1202">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary" data-id="element-1203"></span>
                      Quick installation with minimal disruption
                    </li>
                    <li className="flex items-center gap-2" data-id="element-1204">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary" data-id="element-1205"></span>
                      Lower upfront cost compared to hydronic
                    </li>
                    <li className="flex items-center gap-2" data-id="element-1206">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary" data-id="element-1207"></span>
                      Precise temperature control & zoning
                    </li>
                    <li className="flex items-center gap-2" data-id="element-1208">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary" data-id="element-1209"></span>
                      Compatible with asphalt, concrete, and pavers
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Hydronic System */}
            <div className="rounded-xl border border-slate-700 bg-slate-800/50 overflow-hidden" data-id="element-1210">
              <div className="p-4" data-id="element-1211">
                <h3 className="font-bold text-lg text-white mb-3" data-id="element-1212">
                  Hydronic Heated Driveway Systems
                </h3>
                <div className="text-slate-300 space-y-3" data-id="element-1213">
                  <p data-id="element-1214">
                    Hydronic systems circulate heated fluid (glycol) through
                    tubing beneath the surface. Best suited for large commercial
                    applications or new construction projects.
                  </p>
                  <ul className="space-y-1 text-sm" data-id="element-1215">
                    <li className="flex items-center gap-2" data-id="element-1216">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary" data-id="element-1217"></span>
                      Lower operating costs for very large areas
                    </li>
                    <li className="flex items-center gap-2" data-id="element-1218">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary" data-id="element-1219"></span>
                      Can integrate with existing boiler systems
                    </li>
                    <li className="flex items-center gap-2" data-id="element-1220">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary" data-id="element-1221"></span>
                      Extremely durable PEX tubing
                    </li>
                    <li className="flex items-center gap-2" data-id="element-1222">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary" data-id="element-1223"></span>
                      Ideal for areas over 2,000 sq ft
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}